import { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import moment from 'jalali-moment';

import { LocalStorage } from "../../../services";
import { _post } from "../../../utils/network";
import { commafy } from "../../../utils/methods";

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import CounterItem from '../../../components/Information/CounterItem/CounterItem.component';
import ListTable from "../../../components/List/ListTable/ListTable.component";

const Dashboard = () => {
	const { t } = useTranslation();
	const permissions = LocalStorage.get('permissions').split(',');
	const breadcrumbItems = [
		{ title: t('dashboard') }
	];
	
	const [latestUsers, setLatestUsers] = useState([]);
	const [usersTotalCount, setUsersTotalCount] = useState(0);
	let latestUsersListData = {
		head: [t('fullname'), t('mobile'), t('score1_name'), t('score2_name'), t('status'), t('operation')],
		body: latestUsers.map(user => [
			user.user_fullname,
			user.phone_number,
			commafy(user.user_rial_wallet) || 0,
			commafy(user.user_star_wallet) || 0,
			<span className={"badge rounded-pill p-2 badge-subtle-" + (user.is_active ? 'success' : 'secondary')}>
				{user.is_active ? t('active') : t('deactive')}
			</span>,
			<Link className='btn btn-secondary btn-sm p-1' to={`/users-edit/${user.user_id}`}>
				<i className="fa fa-edit"></i> {t('edit')}
			</Link>
		]),
		directions: ['rtl', 'ltr', 'ltr', 'ltr', '', 'ltr']
	};
	const getLatestUsers = async () => {
		let body = {
			user_fullname: null,
			phone_number: null,
			national_code: null,
			trade_code: null,
			inviter_code: null,
			is_credit: null,
			is_active: null,
			page: 1,
			count: 5
		};
		let response = await _post('/users/admin/getAll', body);
		switch(response.code){
			case 200:
				setLatestUsers(response.data.users);
				setUsersTotalCount(response.data.all_users);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};
	
	const [latestRequests, setLatestRequests] = useState([]);
	const [requestsTotalCount, setRequestsTotalCount] = useState(0);
	let latestRequestsListData = {
		head: [t('creation_date'), t('user'), t('category'), t('status'), t('operation')],
		body: latestRequests.map(request => [
			moment(request.create_date.substring(0, 10), 'YYYY-MM-DD').locale('fa').format('jYYYY/jMM/jDD'),
			request.user_id_information.user_fullname,
			request.requestcategory_id_information.requestcategory_name,
			<span className={"badge rounded-pill p-2 badge-subtle-" + (request.request_state === 0 ? 'success' : request.request_state === 3 ? 'warning' : request.request_state === 1 ? 'primary' : 'danger')}>
				{request.request_state === 0 ? t('pending') : request.request_state === 3 ? t('canceled') : request.request_state === 1 ? t('confirmed') : t('failed')}
			</span>,
			<Link className='btn btn-secondary btn-sm p-1' to={`/requests-edit/${request.request_id}`}>
				<i className="fa fa-edit"></i> {t('edit')}
			</Link>
		]),
		directions: ['ltr', 'rtl', 'rtl', '', 'ltr']
	};
	const getLatestRequests = async () => {
		let body = {
			requestcategory_id: null,
			request_state: null,
			page: 1,
			count: 5
		};
		let response = await _post('/requests/admin/getAll', body);
		switch(response.code){
			case 200:
				setLatestRequests(response.data.requests);
				setRequestsTotalCount(response.data.all_requests);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};
	
	const [latestSignals, setLatestSignals] = useState([]);
	let latestSignalsListData = {
		head: [
			t('creation_date'),
			t('user'),
			t('stock'),
			t('status'),
			t('operation')
		],
		body: latestSignals.map(signal => [
			moment(signal.create_date.substring(0, 10), 'YYYY-MM-DD').locale('fa').format('jYYYY/jMM/jDD'),
			signal.user_id_information.user_fullname,
			<>
				{signal.stock_id_information.stock_information.stock_symbol}
				<span> ( <span className='text-danger'>{commafy(signal.loss_point)}</span> : {commafy(signal.start_point)} : <span className='text-success'>{commafy(signal.profit_point)}</span> ) </span>
			</>,
			<span className={"badge rounded-pill p-2 badge-subtle-" + (signal.signal_state === 0 ? 'success' : signal.signal_state === 1 ? 'info' : signal.signal_state === 2 ? 'primary' : signal.signal_state === 4 ? 'danger' : 'secondary')}>
				{signal.signal_state === 0 ? t('pending') : signal.signal_state === 1 ? t('confirmed') : signal.signal_state === 2 ? t('active') : signal.signal_state === 4 ? t('failed') : t('closed')}
			</span>,
			<Link className='btn btn-secondary btn-sm p-1' to={`/signals-edit/${signal.signal_id}`}>
				<i className="fa fa-edit"></i> {t('edit')}
			</Link>
		]),
		directions: ['ltr', 'rtl', 'rtl', '', 'ltr']
	};
	const [signalsTotalCount, setSignalsTotalCount] = useState(0);
	const getLatestSignals = async () => {
		let body = {
			stock_id: null,
			user_id: null,
			signal_state: null,
			page: 1,
			count: 5
		};
		let response = await _post('/signals/admin/getAll', body);
		switch(response.code){
			case 200:
				setSignalsTotalCount(response.data.all_signals);
				setLatestSignals(response.data.signals);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};
	
	const [latestSignalComments, setLatestSignalComments] = useState([]);
	let latestSignalCommentsListData = {
		head: [t('creation_date'), t('analyser'), t('comment'), t('operation')],
		body: latestSignalComments.map(signalComment => [
			moment(signalComment.create_date.substring(0, 10), 'YYYY-MM-DD').locale('fa').format('jYYYY/jMM/jDD'),
			signalComment.user_id_information.user_fullname,
			signalComment.signalcomment_information.title + ': ' + signalComment.signalcomment_information.description,
			<Link className='btn btn-secondary btn-sm p-1' to={`/signal-comments-edit/${signalComment.signalcomment_id}`}>
				<i className="fa fa-edit"></i> {t('edit')}
			</Link>
		]),
		directions: ['ltr', 'rtl', 'rtl', 'ltr']
	};
	const [signalCommentsTotalCount, setSignalCommentsTotalCount] = useState(0);
	const getSignalComments = async () => {
		let body = {
			signal_id: null,
			page: 1,
			count: 5
		};
		let response = await _post('/signalcomments/admin/getAll', body);
		switch(response.code){
			case 200:
				setSignalCommentsTotalCount(response.data.all_signalcomments);
				setLatestSignalComments(response.data.signalcomments);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};
	
	const [latestConsultations, setLatestConsultations] = useState([]);
	let latestConsultationsListData = {
		head: [t('creation_date'), t('user'), t('category'), t('consultant'), t('user_comment'), t('rate'), t('status'), t('operation')],
		body: latestConsultations.map(consultation => [
			moment(consultation.create_date.substring(0, 10), 'YYYY-MM-DD').locale('fa').format('jYYYY/jMM/jDD'),
			consultation.user_id_information.user_fullname,
			consultation.consultationcategory_id_information.consultationcategory_name,
			consultation.consultant_id_information.consultant_name,
			<span className={"badge rounded-pill p-2 badge-subtle-" + (consultation.comment_status === 0 ? 'secondary' : consultation.comment_status === 1 ? 'primary' : consultation.comment_status === 2 ? 'success' : 'warning')}>
				{consultation.comment_status === 0 ? t('not_sent') : consultation.comment_status === 1 ? t('pending') : consultation.comment_status === 2 ? t('confirmed') : t('failed')}
			</span>,
			<span className={"text-" + ((consultation.user_rate || 5) > 3 ? 'success' : 'danger')}>
				{consultation.user_rate || 5}
			</span>,
			<span className={"badge rounded-pill p-2 badge-subtle-" + (consultation.consultation_state === 0 ? 'success' : consultation.consultation_state === 30 ? 'warning' : consultation.consultation_state === 1 ? 'primary' : consultation.consultation_state === 10 ? 'danger' : 'secondary')}>
				{consultation.consultation_state === 0 ? t('pending') : consultation.consultation_state === 30 ? t('canceled') : consultation.consultation_state === 1 ? t('confirmed') : consultation.consultation_state === 10 ? t('failed') : t('closed')}
			</span>,
			<Link className='btn btn-secondary btn-sm p-1' to={`/consultations-edit/${consultation.consultation_id}`}>
				<i className="fa fa-edit"></i> {t('edit')}
			</Link>
		]),
		directions: ['ltr', 'rtl', 'rtl', 'rtl', '', 'ltr', '', 'ltr']
	};
	const [consultationsTotalCount, setConsultationsTotalCount] = useState(0);
	const getLatestConsultations = async () => {
		let body = {
			consultationcategory_id: null,
			consultant_id: null,
			consultation_state: null,
			comment_status: null,
			page: 1,
			count: 5
		};
		let response = await _post('/consultations/admin/getAll', body);
		switch(response.code){
			case 200:
				setConsultationsTotalCount(response.data.all_consultations);
				setLatestConsultations(response.data.consultations);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	const [latestProductOrders, setLatestProductOrders] = useState([]);
	let latestProductOrdersListData = {
		head: [t('creation_date'), t('user'), t('product'), t('status'), t('operation')],
		body: latestProductOrders.map(productOrder => [
			moment(productOrder.create_date?.substring(0, 10), 'YYYY-MM-DD').locale('fa').format('jYYYY/jMM/jDD'),
			productOrder.user_id_information.user_fullname,
			productOrder.product_id_information.product_name,
			<span className={"badge rounded-pill p-2 badge-subtle-" + ((productOrder.order_state === 0) ? 'primary' : (productOrder.order_state === 1) ? 'secondary' : (productOrder.order_state === 2) ? 'info' : 'danger')}>
				{(productOrder.order_state === 0) ? t('pending') : (productOrder.order_state === 1) ? t('closed') : (productOrder.order_state === 2) ? t('sent') : t('failed')}
			</span>,
			<Link className='btn btn-secondary btn-sm p-1' to={`/product-orders-edit/${productOrder.order_id}`}>
				<i className="fa fa-edit"></i> {t('edit')}
			</Link>
		]),
		directions: ['ltr', 'rtl', 'rtl', '', 'ltr']
	};
	const [productOrdersTotalCount, setProductOrdersTotalCount] = useState(0);
	const getLatestProductOrders = async () => {
		let body = {
			product_id: null,
			user_id: null,
			page: 1,
			count: 5
		};
		let response = await _post('/orders/admin/getAll', body);
		switch(response.code){
			case 200:
				setProductOrdersTotalCount(response.data.all_orders);
				setLatestProductOrders(response.data.orders);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	const [latestContestForecasts, setLatestContestForecasts] = useState([]);
	let latestContestForecastsListData = {
		head: [t('creation_date'), t('contest'), t('user'), t('basket'), t('efficiency')],
		body: latestContestForecasts.map(contestForecast => [
			moment(contestForecast.create_date.substring(0, 10), 'YYYY-MM-DD').locale('fa').format('jYYYY/jMM/jDD'),
			contestForecast.contest_id_information.contest_name,
			contestForecast.user_id_information.user_fullname,
			<>
				{contestForecast.contestenroll_information.map(item => <div key={item.stock_symbol}>{item.stock_symbol} - {item.stock_percent}%</div>)}
			</>,
			contestForecast.user_rank || ''
		]),
		directions: ['ltr', 'rtl', 'rtl', 'rtl', 'ltr']
	};
	const [contestForecastsTotalCount, setContestForecastsTotalCount] = useState(0);
	const getLatestContests = async () => {
		let body = {
			user_id: null,
			contest_id: null,
			sort: 'date',
			page: 1,
			count: 5
		};
		let response = await _post('/contestenrolls/admin/getAll', body);
		switch(response.code){
			case 200:
				setContestForecastsTotalCount(response.data.all_contestenrolls);
				setLatestContestForecasts(response.data.contestenrolls);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	const [latestTransactions, setLatestTransactions] = useState([]);
	let latestTransactionsListData = {
		head: [t('creation_date'), t('user'), t('amount'), t('type'), t('category'), t('description')],
		body: latestTransactions.map(transaction => [
			moment(transaction.create_date.substring(0, 10), 'YYYY-MM-DD').locale('fa').format('jYYYY/jMM/jDD'),
			transaction.user_id_information.user_fullname,
			<span className={transaction.transaction_amount < 0 ? 'text-danger' : 'text-success'}>
				{commafy(transaction.transaction_amount)}
			</span>,
			!!transaction.transaction_type ? t('score2_name') : t('score1_name'),
			transaction.transactioncategory_id_information.transactioncategory_name,
			transaction.transaction_description
		]),
		directions: ['ltr', 'rtl', 'ltr', '', 'rtl', 'rtl']
	};
	const [transactionsTotalCount, setTransactionsTotalCount] = useState(0);
	const getLatestTransactions = async () => {
		let body = {
			user_id: null,
			transaction_type: null,
			transactioncategory_id: null,
			page: 1,
			count: 5
		};
		let response = await _post('/transactions/admin/getAll', body);
		switch(response.code){
			case 200:
				setTransactionsTotalCount(response.data.all_transactions);
				setLatestTransactions(response.data.transactions);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	let counterItems = [
		{ title: t('users_count'), value: commafy(usersTotalCount) || 0 },
		{ title: t('requests_count'), value: commafy(requestsTotalCount) || 0 },
		{ title: t('signals_count'), value: commafy(signalsTotalCount) || 0 },
		{ title: t('signal_comments_count'), value: commafy(signalCommentsTotalCount) || 0 },
		{ title: t('consultations_count'), value: commafy(consultationsTotalCount) || 0 },
		{ title: t('product_orders_count'), value: commafy(productOrdersTotalCount) || 0 },
		{ title: t('contest_forecasts_count'), value: commafy(contestForecastsTotalCount) || 0 },
		{ title: t('transactions_count'), value: commafy(transactionsTotalCount) || 0 }
	];

	useEffect(() => {
		if(permissions.includes('Users'))
			getLatestUsers();
		if(permissions.includes('Requests'))
			getLatestRequests();
		if(permissions.includes('Signals'))
			getLatestSignals();
		if(permissions.includes('Signalcomments'))
			getSignalComments();
		if(permissions.includes('Consultations'))
			getLatestConsultations();
		if(permissions.includes('Orders'))
			getLatestProductOrders();
		if(permissions.includes('Contestenrolls'))
			getLatestContests();
		if(permissions.includes('Transactions'))
			getLatestTransactions();
	}, []);
	
	return <>
		<Breadcrumb items={breadcrumbItems} />
		<CounterItem items={counterItems} />
		{permissions.includes('Users') ?
		<ListTable title={t('latest') + ' ' + t('users')} pageNumber={1} pageCounter={5} totalCount={usersTotalCount} data={latestUsersListData} moreLink="/users-list" tableSm />
		: ''}
		{permissions.includes('Requests') ?
		<ListTable title={t('latest') + ' ' + t('requests')} pageNumber={1} pageCounter={5} totalCount={requestsTotalCount} data={latestRequestsListData} moreLink="/requests-list" tableSm />
		: ''}
		{permissions.includes('Signals') ?
		<ListTable title={t('latest') + ' ' + t('signals')} pageNumber={1} pageCounter={5} totalCount={signalsTotalCount} data={latestSignalsListData} moreLink="/signals-list" tableSm />
		: ''}
		{permissions.includes('Signalcomments') ?
		<ListTable title={t('latest') + ' ' + t('signal_comments')} pageNumber={1} pageCounter={5} totalCount={signalCommentsTotalCount} data={latestSignalCommentsListData} moreLink="/signal-comments-list" tableSm />
		: ''}
		{permissions.includes('Consultations') ?
		<ListTable title={t('latest') + ' ' + t('consultations')} pageNumber={1} pageCounter={5} totalCount={consultationsTotalCount} data={latestConsultationsListData} moreLink="/consultations-list" tableSm />
		: ''}
		{permissions.includes('Orders') ?
		<ListTable title={t('latest') + ' ' + t('product_orders')} pageNumber={1} pageCounter={5} totalCount={productOrdersTotalCount} data={latestProductOrdersListData} moreLink="/product-orders-list" tableSm />
		: ''}
		{permissions.includes('Contestenrolls') ?
		<ListTable title={t('latest') + ' ' + t('forecasts')} pageNumber={1} pageCounter={5} totalCount={contestForecastsTotalCount} data={latestContestForecastsListData} moreLink="/contest-forecasts-list" tableSm />
		: ''}
		{permissions.includes('Transactions') ?
		<ListTable title={t('latest') + ' ' + t('transactions')} pageNumber={1} pageCounter={5} totalCount={transactionsTotalCount} data={latestTransactionsListData} moreLink="/transactions-list" tableSm />
		: ''}
	</>;
};

export default Dashboard;
